/**
 * Structured curriculum data for age-appropriate lessons.
 * Pre-K (3-5): 100% visual + audio, no reading required
 * K-1 (5-7): Visual + audio + simple sight words
 * Grade 2+ (7-10): Reading-based with optional audio
 * 
 * Inspired by Lexia (phonics), Zearn (math), IXL (skills mastery)
 */

// ============ PRE-K PHONICS: Letter Recognition & Sounds ============
export const PREK_LETTERS = [
  { letter: 'A', word: 'Apple', emoji: '🍎', sound: 'ah', color: '#EF4444' },
  { letter: 'B', word: 'Ball', emoji: '⚽', sound: 'buh', color: '#F59E0B' },
  { letter: 'C', word: 'Cat', emoji: '🐱', sound: 'kuh', color: '#10B981' },
  { letter: 'D', word: 'Dog', emoji: '🐕', sound: 'duh', color: '#3B82F6' },
  { letter: 'E', word: 'Egg', emoji: '🥚', sound: 'eh', color: '#8B5CF6' },
  { letter: 'F', word: 'Fish', emoji: '🐟', sound: 'fff', color: '#06B6D4' },
  { letter: 'G', word: 'Grapes', emoji: '🍇', sound: 'guh', color: '#A855F7' },
  { letter: 'H', word: 'Hat', emoji: '🎩', sound: 'huh', color: '#EC4899' },
  { letter: 'I', word: 'Ice cream', emoji: '🍦', sound: 'ih', color: '#14B8A6' },
  { letter: 'J', word: 'Juice', emoji: '🧃', sound: 'juh', color: '#F97316' },
  { letter: 'K', word: 'Kite', emoji: '🪁', sound: 'kuh', color: '#EAB308' },
  { letter: 'L', word: 'Lion', emoji: '🦁', sound: 'lll', color: '#F59E0B' },
  { letter: 'M', word: 'Moon', emoji: '🌙', sound: 'mmm', color: '#6366F1' },
  { letter: 'N', word: 'Nest', emoji: '🪺', sound: 'nnn', color: '#84CC16' },
  { letter: 'O', word: 'Orange', emoji: '🍊', sound: 'oh', color: '#F97316' },
  { letter: 'P', word: 'Pizza', emoji: '🍕', sound: 'puh', color: '#EF4444' },
  { letter: 'Q', word: 'Queen', emoji: '👸', sound: 'kwuh', color: '#A855F7' },
  { letter: 'R', word: 'Rainbow', emoji: '🌈', sound: 'rrr', color: '#EC4899' },
  { letter: 'S', word: 'Sun', emoji: '☀️', sound: 'sss', color: '#EAB308' },
  { letter: 'T', word: 'Tree', emoji: '🌳', sound: 'tuh', color: '#22C55E' },
  { letter: 'U', word: 'Umbrella', emoji: '☂️', sound: 'uh', color: '#3B82F6' },
  { letter: 'V', word: 'Violin', emoji: '🎻', sound: 'vvv', color: '#8B5CF6' },
  { letter: 'W', word: 'Water', emoji: '💧', sound: 'wuh', color: '#06B6D4' },
  { letter: 'X', word: 'Fox', emoji: '🦊', sound: 'ks', color: '#F97316' },
  { letter: 'Y', word: 'Yarn', emoji: '🧶', sound: 'yuh', color: '#EC4899' },
  { letter: 'Z', word: 'Zebra', emoji: '🦓', sound: 'zzz', color: '#1F2937' },
];

// ============ PRE-K MATH: Counting & Numbers ============
export const PREK_NUMBERS = [
  { number: 1, word: 'One', objects: ['🍎'], color: '#EF4444' },
  { number: 2, word: 'Two', objects: ['⭐', '⭐'], color: '#F59E0B' },
  { number: 3, word: 'Three', objects: ['🌸', '🌸', '🌸'], color: '#EC4899' },
  { number: 4, word: 'Four', objects: ['🐟', '🐟', '🐟', '🐟'], color: '#3B82F6' },
  { number: 5, word: 'Five', objects: ['🦋', '🦋', '🦋', '🦋', '🦋'], color: '#8B5CF6' },
  { number: 6, word: 'Six', objects: ['🌙', '🌙', '🌙', '🌙', '🌙', '🌙'], color: '#6366F1' },
  { number: 7, word: 'Seven', objects: ['🍊', '🍊', '🍊', '🍊', '🍊', '🍊', '🍊'], color: '#F97316' },
  { number: 8, word: 'Eight', objects: ['🌟', '🌟', '🌟', '🌟', '🌟', '🌟', '🌟', '🌟'], color: '#EAB308' },
  { number: 9, word: 'Nine', objects: ['🐱', '🐱', '🐱', '🐱', '🐱', '🐱', '🐱', '🐱', '🐱'], color: '#10B981' },
  { number: 10, word: 'Ten', objects: ['💎', '💎', '💎', '💎', '💎', '💎', '💎', '💎', '💎', '💎'], color: '#14B8A6' },
];

// ============ K-1 SIGHT WORDS (Dolch Pre-Primer + Primer) ============
export const K1_SIGHT_WORDS = [
  { word: 'the', sentence: 'I see the cat.', image: '🐱' },
  { word: 'and', sentence: 'Mom and Dad.', image: '👨‍👩‍👧' },
  { word: 'is', sentence: 'It is big.', image: '🐘' },
  { word: 'to', sentence: 'Go to bed.', image: '🛏️' },
  { word: 'in', sentence: 'In the box.', image: '📦' },
  { word: 'it', sentence: 'I like it.', image: '❤️' },
  { word: 'can', sentence: 'I can run.', image: '🏃' },
  { word: 'see', sentence: 'I see you.', image: '👀' },
  { word: 'not', sentence: 'Do not go.', image: '🚫' },
  { word: 'you', sentence: 'I love you.', image: '💕' },
  { word: 'big', sentence: 'A big dog.', image: '🐕' },
  { word: 'red', sentence: 'A red ball.', image: '🔴' },
  { word: 'run', sentence: 'Run fast!', image: '🏃‍♂️' },
  { word: 'up', sentence: 'Go up high.', image: '⬆️' },
  { word: 'my', sentence: 'My toy car.', image: '🚗' },
];

// ============ K-1 CVC WORDS (Consonant-Vowel-Consonant) ============
export const K1_CVC_WORDS = [
  { word: 'cat', sounds: ['c', 'a', 't'], image: '🐱', family: '-at' },
  { word: 'hat', sounds: ['h', 'a', 't'], image: '🎩', family: '-at' },
  { word: 'bat', sounds: ['b', 'a', 't'], image: '🦇', family: '-at' },
  { word: 'dog', sounds: ['d', 'o', 'g'], image: '🐕', family: '-og' },
  { word: 'log', sounds: ['l', 'o', 'g'], image: '🪵', family: '-og' },
  { word: 'bug', sounds: ['b', 'u', 'g'], image: '🐛', family: '-ug' },
  { word: 'mug', sounds: ['m', 'u', 'g'], image: '☕', family: '-ug' },
  { word: 'sun', sounds: ['s', 'u', 'n'], image: '☀️', family: '-un' },
  { word: 'run', sounds: ['r', 'u', 'n'], image: '🏃', family: '-un' },
  { word: 'pen', sounds: ['p', 'e', 'n'], image: '🖊️', family: '-en' },
  { word: 'hen', sounds: ['h', 'e', 'n'], image: '🐔', family: '-en' },
  { word: 'pig', sounds: ['p', 'i', 'g'], image: '🐷', family: '-ig' },
  { word: 'big', sounds: ['b', 'i', 'g'], image: '🐘', family: '-ig' },
  { word: 'fox', sounds: ['f', 'o', 'x'], image: '🦊', family: '-ox' },
  { word: 'box', sounds: ['b', 'o', 'x'], image: '📦', family: '-ox' },
];

// ============ K-1 ADDITION (visual math) ============
export const K1_ADDITION = [
  { a: 1, b: 1, sum: 2, emojiA: '🍎', emojiB: '🍎', color: '#EF4444' },
  { a: 2, b: 1, sum: 3, emojiA: '⭐', emojiB: '⭐', color: '#F59E0B' },
  { a: 2, b: 2, sum: 4, emojiA: '🌸', emojiB: '🌸', color: '#EC4899' },
  { a: 3, b: 1, sum: 4, emojiA: '🐟', emojiB: '🐟', color: '#3B82F6' },
  { a: 3, b: 2, sum: 5, emojiA: '🦋', emojiB: '🦋', color: '#8B5CF6' },
  { a: 4, b: 1, sum: 5, emojiA: '🌙', emojiB: '🌙', color: '#6366F1' },
  { a: 3, b: 3, sum: 6, emojiA: '🍊', emojiB: '🍊', color: '#F97316' },
  { a: 4, b: 2, sum: 6, emojiA: '🎈', emojiB: '🎈', color: '#EF4444' },
  { a: 4, b: 3, sum: 7, emojiA: '🌟', emojiB: '🌟', color: '#EAB308' },
  { a: 5, b: 3, sum: 8, emojiA: '💎', emojiB: '💎', color: '#14B8A6' },
];

// ============ K-1 SUBTRACTION (visual math) ============
export const K1_SUBTRACTION = [
  { total: 3, take: 1, diff: 2, emoji: '🍎', color: '#EF4444' },
  { total: 4, take: 1, diff: 3, emoji: '⭐', color: '#F59E0B' },
  { total: 5, take: 2, diff: 3, emoji: '🌸', color: '#EC4899' },
  { total: 4, take: 2, diff: 2, emoji: '🐟', color: '#3B82F6' },
  { total: 5, take: 3, diff: 2, emoji: '🦋', color: '#8B5CF6' },
  { total: 6, take: 2, diff: 4, emoji: '🌙', color: '#6366F1' },
  { total: 6, take: 3, diff: 3, emoji: '🍊', color: '#F97316' },
  { total: 7, take: 4, diff: 3, emoji: '🎈', color: '#EF4444' },
  { total: 8, take: 3, diff: 5, emoji: '🌟', color: '#EAB308' },
  { total: 10, take: 5, diff: 5, emoji: '💎', color: '#14B8A6' },
];

// ============ K-1 SHAPES ============
export const K1_SHAPES = [
  { name: 'Circle', emoji: '⭕', sides: 0, desc: 'Round with no corners', color: '#EF4444' },
  { name: 'Square', emoji: '🟧', sides: 4, desc: 'Four equal sides', color: '#F59E0B' },
  { name: 'Triangle', emoji: '🔺', sides: 3, desc: 'Three sides and three corners', color: '#10B981' },
  { name: 'Rectangle', emoji: '🟦', sides: 4, desc: 'Two long sides and two short sides', color: '#3B82F6' },
  { name: 'Star', emoji: '⭐', sides: 10, desc: 'Five points', color: '#EAB308' },
  { name: 'Heart', emoji: '❤️', sides: 0, desc: 'A love shape', color: '#EC4899' },
  { name: 'Diamond', emoji: '🔷', sides: 4, desc: 'A tilted square', color: '#6366F1' },
  { name: 'Oval', emoji: '🥚', sides: 0, desc: 'A stretched circle', color: '#14B8A6' },
];

// ============ GRADE 2-4 READING COMPREHENSION ============
export const GRADE2_PASSAGES = [
  {
    title: 'The Hungry Caterpillar',
    text: 'A small caterpillar was very hungry. It ate one apple on Monday. It ate two pears on Tuesday. By Friday, it had eaten so much food! Then it built a cocoon. After two weeks, it became a beautiful butterfly.',
    questions: [
      { q: 'What was the caterpillar?', options: ['Happy', 'Hungry', 'Sleepy', 'Angry'], answer: 1 },
      { q: 'What did it eat on Monday?', options: ['Pears', 'Oranges', 'An apple', 'Cake'], answer: 2 },
      { q: 'What did it become?', options: ['A bird', 'A butterfly', 'A frog', 'A bee'], answer: 1 },
    ]
  },
];

// ============ SKILL MAPS ============
export const SKILL_MAPS = {
  'prek': {
    reading: [
      { id: 'letter-names', name: 'Letter Names', icon: '🔤', lessonCount: 26, desc: 'Learn all 26 letters' },
      { id: 'letter-sounds', name: 'Letter Sounds', icon: '🔊', lessonCount: 26, desc: 'Hear how letters sound' },
      { id: 'match-sounds', name: 'Match Sounds', icon: '🎯', lessonCount: 13, desc: 'Match letters to pictures' },
    ],
    math: [
      { id: 'counting', name: 'Counting', icon: '🔢', lessonCount: 10, desc: 'Count objects 1 to 10' },
      { id: 'number-names', name: 'Number Names', icon: '🏷️', lessonCount: 10, desc: 'Recognize numbers' },
      { id: 'comparing', name: 'More or Less', icon: '⚖️', lessonCount: 5, desc: 'Which group has more?' },
    ]
  },
  'k1': {
    reading: [
      { id: 'sight-words', name: 'Sight Words', icon: '👀', lessonCount: 15, desc: 'Common words to know' },
      { id: 'cvc-words', name: 'Sound It Out', icon: '🗣️', lessonCount: 15, desc: 'Blend sounds into words' },
      { id: 'sentences', name: 'Read Sentences', icon: '📖', lessonCount: 10, desc: 'Put words together' },
    ],
    math: [
      { id: 'addition', name: 'Addition', icon: '➕', lessonCount: 10, desc: 'Adding numbers together' },
      { id: 'subtraction', name: 'Subtraction', icon: '➖', lessonCount: 10, desc: 'Taking numbers away' },
      { id: 'shapes', name: 'Shapes', icon: '🔷', lessonCount: 8, desc: 'Learn shapes and patterns' },
    ]
  },
  'grade2': {
    reading: [
      { id: 'comprehension', name: 'Reading Stories', icon: '📚', lessonCount: 10, desc: 'Read and understand' },
      { id: 'vocabulary', name: 'New Words', icon: '📝', lessonCount: 10, desc: 'Build your word bank' },
    ],
    math: [
      { id: 'multiply', name: 'Multiplication', icon: '✖️', lessonCount: 10, desc: 'Groups of numbers' },
      { id: 'fractions', name: 'Fractions', icon: '🍰', lessonCount: 8, desc: 'Parts of a whole' },
    ]
  }
};

// Helper to get grade tier from age or explicit grade_level override.
// Accepts EITHER an age_group string (legacy) or a full child object (preferred).
// Precedence: child.grade_level → child.age_group → age band from birth_date → 'prek'.
export const getGradeTier = (input) => {
  // If we got a full child dict, honour the parent-set override first.
  if (input && typeof input === 'object') {
    const explicit = (input.grade_level || '').toString().trim().toUpperCase();
    if (explicit) {
      if (['PK', 'PRE-K', 'PREK'].includes(explicit)) return 'prek';
      if (['K', 'KG', 'KINDERGARTEN', '1', '1ST'].includes(explicit)) return 'k1';
      if (['2', '2ND', '3', '3RD'].includes(explicit)) return 'grade2';
      if (['4', '4TH', '5', '5TH', '6', '6TH', '7', '7TH', '8', '8TH'].includes(explicit)) return 'grade2';
    }
    // Fallback to age_group
    if (input.age_group) return getGradeTier(input.age_group);
    // Fallback to computed age from birth_date
    if (input.birth_date) {
      try {
        const bd = new Date(input.birth_date);
        const now = new Date();
        const months = (now.getFullYear() - bd.getFullYear()) * 12 + (now.getMonth() - bd.getMonth());
        if (months < 60)  return 'prek';   // 3-4
        if (months < 84)  return 'k1';     // 5-6 (Kindergarten)
        if (months < 108) return 'k1';     // 7-8 (Early Elementary — same appropriate content)
        return 'grade2';                    // 9+
      } catch { /* noop */ }
    }
    return 'prek';
  }

  // Legacy: raw age_group string
  if (!input) return 'prek';
  const ag = input.toString().toLowerCase();
  if (ag.includes('3-') || ag.includes('4-') || ag.includes('pre') || ag.includes('toddler') || ag.includes('infant')) return 'prek';
  if (ag.includes('5-') || ag.includes('6-') || ag.includes('kindergarten') || ag.includes('k-') || ag.includes('early')) return 'k1';
  return 'grade2';
};

// Ordered tiers so we can show "Coming up next!" for anything above the child's band.
export const TIER_ORDER = ['prek', 'k1', 'grade2'];

export const TIER_LABEL = {
  prek: 'Pre-K',
  k1: 'Kindergarten & 1st',
  grade2: '2nd Grade & up',
};
